import React, { useState } from "react";
import { MdKeyboardReturn, MdCancel } from "react-icons/md";
import { updateOrder } from "../../services/order.service";
import { toast } from "react-toastify";
import { ThemedCard } from "../../helpers/csshelper.js";


export default function ReturnOrderButton({ order, bussinessUuid, theme, onSuccess, setPageLoading }) {
  const [returnModal, setReturnModal] = useState({
    isOpen: false,
    reason: ""
  });

  // Only show for delivered orders
  if (order.orderStatus !== "Delivered") return null;

  // Check return period eligibility
  const deliveryDate = order.deliveredDate || order.updatedAt;
  const returnExpirationDate = new Date(deliveryDate);

  let maxReturnDays = 0;
  let anyItemReturnable = false;

  if (order.items && order.items.length > 0) {
    order.items.forEach(item => {
      if (item.showReturn !== false) {
        anyItemReturnable = true;
        let days = 7; // default
        if (item.returnPeriod) {
          const match = item.returnPeriod.match(/(\d+)/);
          if (match) {
            days = parseInt(match[1]);
          }
        }
        if (days > maxReturnDays) maxReturnDays = days;
      }
    });
  } else {
    anyItemReturnable = true;
    maxReturnDays = 7;
  }

  returnExpirationDate.setDate(returnExpirationDate.getDate() + maxReturnDays);
  const isExpired = new Date() > returnExpirationDate;
  const canReturn = anyItemReturnable && !isExpired;

  const handleReturnOrder = () => {
    setReturnModal({ isOpen: true, reason: "" });
  };

  const submitReturnOrder = async () => {
    if (!returnModal.reason.trim()) {
      toast.error("Please provide a reason for return");
      return;
    }

    try {
      setPageLoading(true);
      const res = await updateOrder(bussinessUuid, order.customerId, order.orderId, {
        orderStatus: "Pending return",
        returnReason: returnModal.reason
      });
      if (res) {
        toast.success("Return requested successfully");
        setReturnModal({ isOpen: false, reason: "" });
        onSuccess();
      }
    } catch (error) {
      toast.error("Failed to request return");
      setPageLoading(false);
    }
  };

  return (
    <>
      {/* Return Button */}
      <button
        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 border ${
          !canReturn ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        onClick={() => canReturn && handleReturnOrder()}
        disabled={!canReturn}
        title={!canReturn ? "Return period has expired or items are not eligible for return" : "Return Order"}
        style={{
          backgroundColor: canReturn ? '#fef3c7' : '#f3f4f6',
          color: canReturn ? '#f59e0b' : '#9ca3af',
          borderColor: canReturn ? '#fcd34d' : '#e5e7eb',
          borderRadius: theme?.borderRadius || '8px',
          fontFamily: theme?.fontFamily || 'Inter',
        }}
      >
        <MdKeyboardReturn className='text-lg' />
        {isExpired ? 'Return Window Closed' : 'Return Order'}
      </button>

      {/* Return Reason Modal */}
      {returnModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <ThemedCard className="w-full max-w-md p-6 relative animate-fadeIn" theme={theme}>
            <button
              onClick={() => setReturnModal({ isOpen: false, reason: "" })}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1 transition-colors"
            >
              <MdCancel size={24} />
            </button>
            <h2 className="text-xl font-bold mb-4" style={{ color: theme?.headingColor || '#111827', fontFamily: theme?.fontFamily || 'Inter' }}>
              Return Order
            </h2>
            <p className="text-sm mb-4" style={{ color: theme?.textColor + '80' || '#6b7280', fontFamily: theme?.fontFamily || 'Inter' }}>
              Please provide a reason for returning this order.
            </p>
            <textarea
              className="w-full border rounded-lg p-3 outline-none focus:ring-2 min-h-[100px] resize-none mb-4"
              style={{
                borderColor: theme?.primaryColor ? `${theme.primaryColor}30` : '#bfdbfe',
                fontFamily: theme?.fontFamily || 'Inter',
                focusRingColor: theme?.primaryColor || '#4f46e5'
              }}
              placeholder="e.g., Item was defective, changed my mind..."
              value={returnModal.reason}
              onChange={(e) => setReturnModal(prev => ({ ...prev, reason: e.target.value }))}
            />
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setReturnModal({ isOpen: false, reason: "" })}
                className="px-4 py-2 rounded-lg font-medium transition-colors border"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#4b5563',
                  borderColor: '#d1d5db',
                  fontFamily: theme?.fontFamily || 'Inter'
                }}
              >
                Cancel
              </button>
              <button
                onClick={submitReturnOrder}
                disabled={!returnModal.reason.trim()}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  !returnModal.reason.trim()
                    ? 'opacity-50 cursor-not-allowed'
                    : 'shadow-md hover:shadow-lg'
                }`}
                style={{
                  backgroundColor: theme?.primaryColor || '#4f46e5',
                  color: '#ffffff',
                  fontFamily: theme?.fontFamily || 'Inter',
                  borderRadius: theme?.borderRadius || '8px',
                }}
              >
                Submit Return
              </button>
            </div>
          </ThemedCard>
        </div>
      )}
    </>
  );
}
